import { ReactElement, useEffect } from "react"
import styled from "@emotion/styled"
import { useTranslation } from "../TranslationProvider"
import { zIndex } from "../lib/theme"
import { lockScroll, unlockScroll } from "../lib/scrollLock"

type IReview = {
  date: string
  name: string
  title: string
  text: string
  rating: number
}

interface FullscreenReviewViewerProps {
  review: IReview
  reviewIndex: number
  totalReviews: number
  isOpen: boolean
  onClose: () => void
  onNext: () => void
  onPrev: () => void
}

export default function FullscreenReviewViewer({
  review,
  reviewIndex,
  totalReviews,
  isOpen,
  onClose,
  onNext,
  onPrev,
}: FullscreenReviewViewerProps): ReactElement | null {
  const { t } = useTranslation()

  // Handle scroll lock
  useEffect(() => {
    if (isOpen) {
      lockScroll()
    } else {
      unlockScroll()
    }

    return () => {
      unlockScroll()
    }
  }, [isOpen])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return

      if (event.key === "Escape") {
        onClose()
      } else if (event.key === "ArrowRight") {
        onNext()
      } else if (event.key === "ArrowLeft") {
        onPrev()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, onClose, onNext, onPrev])

  // Reset scroll position when modal opens or review changes
  useEffect(() => {
    if (isOpen) {
      // Find the review container and scroll to top
      const reviewContainer = document.querySelector("[data-review-container]")
      if (reviewContainer) {
        reviewContainer.scrollTop = 0
      }
    }
  }, [isOpen, reviewIndex])

  if (!isOpen) return null

  const { date, name, title, text, rating } = review

  const ratingArray = [
    ...Array.from({ length: Number(rating) }, (_, idx) => (
      <div key={`blueStar${idx}`}>{blueStar}</div>
    )),
    ...Array.from({ length: 5 - Number(rating) }, (_, idx) => (
      <div key={`grayStar${idx}`}>{grayStar}</div>
    )),
  ]

  return (
    <FullscreenOverlay onClick={onClose}>
      <CloseButton
        onClick={onClose}
        aria-label={t("accessibility.closeFullscreen")}>
        ×
      </CloseButton>

      <NavigationButton
        className="prev"
        onClick={(e) => {
          e.stopPropagation()
          onPrev()
        }}
        aria-label={t("accessibility.previousReview") || "Previous review"}>
        {chevronLeft}
      </NavigationButton>

      <NavigationButton
        className="next"
        onClick={(e) => {
          e.stopPropagation()
          onNext()
        }}
        aria-label={t("accessibility.nextReview") || "Next review"}>
        {chevronRight}
      </NavigationButton>

      <ReviewContainer
        data-review-container
        onClick={(e) => {
          e.stopPropagation()
          onNext()
        }}>
        <FullscreenReview>
          <ReviewHeader>
            <ReviewDate>{date}</ReviewDate>
            <ReviewRating>{ratingArray}</ReviewRating>
          </ReviewHeader>

          <ReviewBody>
            <ReviewTitle>{title}</ReviewTitle>
            <ReviewText>{text}</ReviewText>
            <ReviewName>- {name}</ReviewName>
          </ReviewBody>

          <ReviewCounter>
            {reviewIndex} / {totalReviews}
          </ReviewCounter>
        </FullscreenReview>
      </ReviewContainer>
    </FullscreenOverlay>
  )
}

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.modal};
  cursor: pointer;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ReviewContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
  display: flex;
  justify-content: center;
  align-items: flex-start; /* Changed from center to flex-start */
  overflow-y: auto;
  overflow-x: hidden; /* Prevent horizontal scrolling */
  padding: 2rem;

  /* Ensure scrolling starts from top and is smooth */
  scroll-behavior: smooth;
  overscroll-behavior: contain;

  @media only screen and (max-width: 600px) {
    padding: 1rem;
    /* On mobile, ensure we can scroll to see all content */
    align-items: flex-start;
  }
`

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: var(--white);
  color: var(--tertiary);
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
  z-index: 1;
  user-select: none;

  &:hover {
    background-color: var(--white);
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid var(--special);
    outline-offset: 2px;
  }

  @media only screen and (max-width: 600px) {
    top: 15px;
    right: 15px;
    width: 35px;
    height: 35px;
    font-size: 20px;
  }
`

const NavigationButton = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--tertiary);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
  z-index: 1;

  &.prev {
    left: 20px;
  }

  &.next {
    right: 20px;
  }

  &:hover {
    background-color: var(--white);
    transform: translateY(-50%) scale(1.1);
  }

  &:focus {
    outline: 2px solid var(--special);
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media only screen and (max-width: 600px) {
    width: 40px;
    height: 40px;

    &.prev {
      left: 15px;
    }

    &.next {
      right: 15px;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const FullscreenReview = styled.div`
  min-width: 50vw;
  max-width: 60rem;
  min-height: 15rem;
  padding: 2rem;
  border-radius: 0.5rem;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.5rem;

  background-color: var(--white);
  color: black;
  font-family: "Questrial", sans-serif;

  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease-out;

  user-select: none;

  @keyframes scaleIn {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @media only screen and (max-width: 600px) {
    min-width: 80vw;
    padding: 1.5rem;
  }
`

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const ReviewDate = styled.div`
  color: darkgray;
  font-size: 18px;

  @media only screen and (max-width: 600px) {
    font-size: 16px;
  }
`

const ReviewRating = styled.div`
  width: 12rem;

  display: flex;
  justify-content: flex-end;

  svg {
    max-height: 2rem;

    @media only screen and (max-width: 600px) {
      max-height: 1.7rem;
    }
  }
`

const ReviewBody = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 1rem;

  color: black;
`

const ReviewTitle = styled.h3`
  margin: 0;
  text-align: left;
  font-size: 24px;
  font-weight: 600;

  @media only screen and (max-width: 600px) {
    font-size: 20px;
  }
`

const ReviewText = styled.div`
  text-align: left;
  font-size: 18px;
  line-height: 1.6;

  @media only screen and (max-width: 600px) {
    font-size: 16px;
  }
`

const ReviewName = styled.div`
  justify-self: flex-end;
  text-align: right;
  font-size: 20px;
  font-weight: 500;

  @media only screen and (max-width: 600px) {
    font-size: 18px;
  }
`

const ReviewCounter = styled.div`
  text-align: center;
  font-size: 14px;
  color: darkgray;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
`

const chevronLeft = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="15,18 9,12 15,6"></polyline>
  </svg>
)

const chevronRight = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="9,18 15,12 9,6"></polyline>
  </svg>
)

const blueStar = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="rebeccapurple">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14l-5-4.87 6.91-1.01L12 2z" />
  </svg>
)

const grayStar = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="lightgray">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14l-5-4.87 6.91-1.01L12 2z" />
  </svg>
)
